import { Suspense, useEffect, useState } from "react";
import { Outlet, useRouteError } from "react-router";
import { RotateCw } from "lucide-react";

import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { EmptyState } from "@/components/ui/EmptyState";
import { Skeleton } from "@/components/ui/Skeleton";

/** How long a screen's code may take before anything is drawn in its place. */
const QUIET_FOR = 200;

/**
 * How each browser words a script it could not fetch, and how the bundler
 * words the stylesheet that goes with one.
 */
const NOT_FETCHED =
  /dynamically imported module|importing a module script failed|unable to preload css/i;

/**
 * Where a screen goes, inside the frame it belongs to.
 *
 * Each screen's code is fetched the first time somebody opens it. The frame
 * around it — the sidebar, the top bar, the sign-in card — is already here
 * and stays exactly where it is; only the page waits.
 */
export function ScreenBoundary() {
  return (
    <Suspense fallback={<ScreenFallback />}>
      <Outlet />
    </Suspense>
  );
}

/**
 * What stands in the page while a screen's code is on its way.
 *
 * Nothing at all, at first. A screen that has been opened before comes out of
 * the browser's cache in less time than a placeholder takes to be noticed,
 * and a placeholder that appears and vanishes inside a blink is a flicker
 * with a reason. Only when the wait is long enough to wonder about does the
 * outline of a page appear: a title and a few lines, no wider than whatever
 * it is standing in.
 */
export function ScreenFallback() {
  const [waited, setWaited] = useState(false);

  useEffect(() => {
    const timer = window.setTimeout(() => setWaited(true), QUIET_FOR);
    return () => window.clearTimeout(timer);
  }, []);

  if (!waited) return null;
  return (
    <div role="status">
      <span className="sr-only">Loading</span>
      <Skeleton className="h-7 w-2/5 max-w-56" />
      <div className="mt-6 space-y-3">
        <Skeleton className="h-4 w-full max-w-2xl" />
        <Skeleton className="h-4 w-5/6 max-w-xl" />
        <Skeleton className="h-4 w-2/3 max-w-md" />
      </div>
    </div>
  );
}

/**
 * What is shown when a screen cannot be drawn.
 *
 * The usual cause is a tab left open across a release. Every file the build
 * makes is named after its contents, so after a release the names this tab
 * knows are gone from the server and the screen it asks for is a 404. Trying
 * again asks for the same name. The one thing that works is loading the page
 * again, which fetches the new list of names, so that is the one thing
 * offered.
 *
 * Anything else that throws while a screen is drawing lands here too, with
 * the same way out, rather than on the router's own page of stack trace.
 */
export function ScreenError({
  whole = false,
}: {
  /** The whole window, for a failure outside any frame. */
  whole?: boolean;
}) {
  const error = useRouteError();
  const released = error instanceof Error && NOT_FETCHED.test(error.message);

  return (
    <div className={cn(whole && "flex min-h-dvh items-center justify-center p-6")}>
      <div role="alert">
        <EmptyState
          icon={<RotateCw className="size-5" />}
          title={released ? "This screen has been updated" : "This screen could not be shown"}
          description={
            released
              ? "A newer version of the system was released while this tab was open. Reload the page to carry on."
              : "Reload the page. If it keeps happening, tell HR what you were doing."
          }
          action={
            <Button variant="primary" onClick={() => window.location.reload()}>
              Reload
            </Button>
          }
        />
      </div>
    </div>
  );
}
